import { IdentityDelta } from 'lib/reservedQuantity'
import {
  ArticleIdentityFields,
  CountryEdition,
  FINISH_TYPE_LABELS,
  getArticleIdentityKey,
  getIdentityKeyFromFields,
} from 'shared'
import { create } from 'zustand'

export interface ChangesContentInterface {
  quantity: number
  condition: string
  idLanguage: number
  isSigned: boolean
  isFirstEd: boolean
  locationName: string
  finishType: FINISH_TYPE_LABELS
  countryEdition: CountryEdition
  deltaQuantity?: number
  isNewRow?: boolean
  rowId: string
  gradingCompany?: string | undefined
  grade?: string | undefined
}

export interface ChangesInterface {
  [key: string]: ChangesContentInterface
}

export interface LocationStoreState {
  changes: any[]
  currentValues: ChangesInterface
  resetLocationRows: boolean
  newRows: ChangesContentInterface[]
  setNewRows: (newList: ChangesContentInterface[]) => void
  setChanges: (newChanges: any[]) => void
  setCurrentValues: (newValues: ChangesInterface) => void
  setResetLocationRows: (newResetLocationRows: boolean) => void
}

const getRowIdentityFields = (row: ChangesContentInterface): ArticleIdentityFields => ({
  condition: row.condition,
  idLanguage: row.idLanguage,
  finishType: row.finishType,
  isSigned: row.isSigned,
  isFirstEd: row.isFirstEd,
  countryEdition: row.countryEdition,
  gradingCompany: row.gradingCompany,
  grade: row.grade,
})

// Translate the panel's currentValues into per-identity quantity deltas, mirroring what
// handleConfirmLocationsPanel will actually save:
//   - existing row, no identity change → only deltaQuantity counts (pure quantityToEdit
//     edits without an attribute change are silently discarded by getChanges).
//   - existing row, identity change → quantityToEdit units move from the original
//     identity to the new one.
//   - new row → +deltaQuantity at the new row's identity.
export const buildPanelDeltas = (
  currentValues: ChangesInterface,
  initialValues: ChangesInterface
): IdentityDelta[] => {
  const deltas: IdentityDelta[] = []

  for (const [key, current] of Object.entries(currentValues)) {
    const currentKey = getIdentityKeyFromFields(getRowIdentityFields(current))

    if (current.isNewRow) {
      const delta = current.deltaQuantity ?? 0
      if (delta !== 0) deltas.push({ identityKey: currentKey, delta })
      continue
    }

    const initial = initialValues[key]
    if (!initial) continue

    const initialKey = getIdentityKeyFromFields(getRowIdentityFields(initial))

    if (initialKey === currentKey) {
      const delta = current.deltaQuantity ?? 0
      if (delta !== 0) deltas.push({ identityKey: currentKey, delta })
    } else {
      const moved = current.quantity ?? 0
      if (moved !== 0) {
        deltas.push({ identityKey: initialKey, delta: -moved })
        deltas.push({ identityKey: currentKey, delta: moved })
      }
    }
  }

  return deltas
}

export { getArticleIdentityKey }

export const useLocationStore = create<LocationStoreState>()((set) => ({
  changes: [],
  currentValues: {},
  resetLocationRows: false,
  newRows: [],
  setNewRows: (newList: ChangesContentInterface[]) => set({ newRows: newList }),
  setChanges: (newChanges: any[]) => set({ changes: newChanges }),
  setCurrentValues: (newValues: ChangesInterface) => set({ currentValues: newValues }),
  setResetLocationRows: (newResetLocationRow) =>
    set((s) =>
      s.resetLocationRows === newResetLocationRow ? s : { resetLocationRows: newResetLocationRow }
    ),
}))
